import type { HumanProportions, HumanSkull } from './human.js';

interface FeatureVariant { id: string; name: string; legacyIds?: string[] }
interface MouthVariant extends FeatureVariant { shape?: Partial<HumanSkull['mouth']> }
interface EyeVariant extends FeatureVariant { size?: number; opening?: number; tilt?: number }
interface BrowVariant extends FeatureVariant { shape?: HumanSkull['eyebrows'] }
interface NoseVariant extends FeatureVariant { width?: number; length?: number; projection?: number; bridge?: number }

/** Relative edits preserve each body's anatomy and compose with every face morph. */
export const humanoidMouths: MouthVariant[] = [
  { id: 'standard', name: 'Natural' },
  { id: 'full', name: 'Full lips', shape: { upper: 1.28, lower: 1.24, forward: 1.18 } },
  { id: 'thin', name: 'Thin lips', shape: { upper: .64, lower: .67, forward: .8 } },
  { id: 'wide', name: 'Wide', shape: { width: 1.18, upper: .93, lower: .93 } },
  { id: 'petite', name: 'Petite', shape: { width: .84, upper: .94, lower: .96 } },
  { id: 'bowed', name: "Cupid’s bow", shape: { bow: 1.85, upper: 1.1, lower: .94 } },
];
export const humanoidEyes: EyeVariant[] = [
  { id: 'standard', name: 'Natural' },
  { id: 'round', name: 'Round', size: 1.04, opening: 1.27 },
  { id: 'narrow', name: 'Narrow', opening: .68 },
  { id: 'hooded', name: 'Hooded', opening: .83, size: .96 },
  { id: 'upturned', name: 'Upturned', tilt: .14 },
  { id: 'downturned', name: 'Downturned', tilt: -.12 },
];
export const humanoidEyebrows: BrowVariant[] = [
  { id: 'standard', name: 'Straight', legacyIds: ['straight'] },
  { id: 'arched', name: 'High arch', shape: { arch: 1.65, thickness: .9 } },
  { id: 'angled', name: 'Angled', shape: { angular: true, arch: 1.3, slope: -.0015 } },
  { id: 'thick', name: 'Thick', shape: { thickness: 1.5, arch: .8 } },
  { id: 'fine', name: 'Fine', shape: { thickness: .55, arch: 1.15 } },
  { id: 'none', name: 'None', shape: { thickness: 0 } },
];
export const humanoidNoses: NoseVariant[] = [
  { id: 'standard', name: 'Natural' },
  { id: 'button', name: 'Button', width: .92, length: .87, projection: -.0035 },
  { id: 'broad', name: 'Broad', width: 1.2, projection: -.001 },
  { id: 'narrow', name: 'Narrow', width: .8, projection: .0015 },
  { id: 'hooked', name: 'Hooked', length: 1.08, bridge: .004, projection: .0025 },
  { id: 'upturned', name: 'Upturned', length: .89, projection: .003 },
];
export const FACE_FEATURE_SLOTS = ['mouth', 'eyes', 'eyebrows', 'nose'] as const;

/** A separate cached sculpt per chosen combination; authored rigs and skulls are never mutated. */
export function withFaceFeatures(source: HumanProportions, parts: Record<string, string>): HumanProportions {
  const mouth = humanoidMouths.find(v => v.id === parts.mouth) ?? humanoidMouths[0];
  const eyes = humanoidEyes.find(v => v.id === parts.eyes) ?? humanoidEyes[0];
  const eyebrows = humanoidEyebrows.find(v => v.id === parts.eyebrows) ?? humanoidEyebrows[0];
  const nose = humanoidNoses.find(v => v.id === parts.nose) ?? humanoidNoses[0];
  const ids = [mouth.id, eyes.id, eyebrows.id, nose.id];
  if (ids.every(id => id === 'standard')) return source;
  const skull = { ...source.skull, mouth: { ...source.skull.mouth }, eye: { ...source.skull.eye }, nose: { ...source.skull.nose },
    eyebrows: { ...source.skull.eyebrows, ...eyebrows.shape } };
  for (const [key, multiplier] of Object.entries(mouth.shape ?? {})) skull.mouth[key as keyof HumanSkull['mouth']] *= multiplier;
  if (eyes.opening !== undefined) skull.eye.opening = (skull.eye.opening ?? 1) * eyes.opening;
  skull.nose.length *= nose.length ?? 1;
  skull.nose.tipZ += nose.projection ?? 0;
  const face = { ...source.face, eyeSize: source.face.eyeSize * (eyes.size ?? 1), eyeTilt: source.face.eyeTilt + (eyes.tilt ?? 0),
    nose: source.face.nose * (nose.width ?? 1), bridge: source.face.bridge + (nose.bridge ?? 0) };
  const anatomyIds = [mouth.id, eyes.id, nose.id];
  return { ...source, skull, face, headCacheKey: anatomyIds.every(id => id === 'standard') ? source.headCacheKey : `${source.headCacheKey ?? source.id}:features:${anatomyIds.join(':')}` };
}
